import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
} from 'framer-motion'
import { useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import ArticleCover from './ArticleCover'
import { articleSlug, type Article } from '../../data/articles'
import { useT, useLocale } from '../../i18n/LocaleContext'
import { localizedPath } from '../../i18n/routes'
import { formatDateLocalized } from '../../lib/localizeHtml'

type Props = {
  article: Article
  size?: 'sm' | 'md' | 'lg'
  delay?: number
}

export default function ArticleCard({ article, size = 'md', delay = 0 }: Props) {
  const { t } = useT()
  const locale = useLocale()
  const meta = article.i18n[locale]
  const [hovered, setHovered] = useState(false)

  // Tilt based on cursor position
  const rotateX = useSpring(useMotionValue(0), { stiffness: 150, damping: 18 })
  const rotateY = useSpring(useMotionValue(0), { stiffness: 150, damping: 18 })

  // Spotlight position
  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    mouseX.set(x)
    mouseY.set(y)

    const cx = rect.width / 2
    const cy = rect.height / 2
    const ry = ((x - cx) / cx) * 4
    const rx = -((y - cy) / cy) * 4
    rotateX.set(rx)
    rotateY.set(ry)
  }

  const handleLeave = () => {
    setHovered(false)
    mouseX.set(-500)
    mouseY.set(-500)
    rotateX.set(0)
    rotateY.set(0)
  }

  const coverAspect = size === 'lg' ? 'aspect-[16/10]' : 'aspect-[4/3]'
  const titleSize =
    size === 'lg' ? 'text-2xl sm:text-3xl' : size === 'sm' ? 'text-base' : 'text-xl'

  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.22), transparent 65%)`
  const borderGlow = useMotionTemplate`radial-gradient(260px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.8), transparent 65%)`

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ perspective: 1200 }}
    >
      <motion.div
        onMouseEnter={() => setHovered(true)}
        onMouseMove={handleMove}
        onMouseLeave={handleLeave}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className="group relative"
      >
        {/* Animated blue border shell */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: borderGlow }}
        />

        <Link
          to={localizedPath('blog.article', locale, { slug: articleSlug(article, locale) })}
          className="relative block rounded-2xl border border-ink-100 bg-ink-100 overflow-hidden transition-shadow duration-500 group-hover:shadow-[0_30px_80px_-30px_rgba(59,93,255,0.35),0_8px_24px_-12px_rgba(14,14,20,0.15)] group-hover:border-accent-200"
        >
          {/* Cover */}
          <div className={`relative overflow-hidden ${coverAspect}`}>
            <motion.div
              animate={{ scale: hovered ? 1.06 : 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0"
            >
              <ArticleCover cover={article.cover} hovered={hovered} className="w-full h-full" />
            </motion.div>

            {/* Category pill */}
            <div className="absolute top-3 left-3 z-10">
              <motion.span
                animate={{ y: hovered ? -2 : 0 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center rounded-full bg-white/95 backdrop-blur text-ink-900 text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1 border border-white/60"
              >
                {t('blog.categories.' + article.category)}
              </motion.span>
            </div>

            {/* Read time */}
            <div className="absolute top-3 right-3 z-10">
              <span className="inline-flex items-center gap-1 rounded-full bg-ink-950/60 backdrop-blur text-white text-[10px] font-medium px-2 py-1">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" strokeLinecap="round" />
                </svg>
                {t('blog.readTimeShort', { n: article.readTime })}
              </span>
            </div>

            {/* Arrow cue — slides in on hover */}
            <motion.div
              initial={false}
              animate={{
                opacity: hovered ? 1 : 0,
                x: hovered ? 0 : -8,
                y: hovered ? 0 : 8,
              }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-3 right-3 z-10 w-10 h-10 rounded-full bg-accent-700 text-white flex items-center justify-center shadow-[0_12px_30px_-6px_rgba(59,93,255,0.6)]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>
          </div>

          {/* Body */}
          <div className="relative p-5 sm:p-6">
            {/* Spotlight on body */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{ background: spotlight }}
            />

            <div className="relative">
              <div className="flex items-center gap-2 text-xs text-ink-500">
                <span>{formatDateLocalized(article.date, locale)}</span>
                <span className="text-ink-300">·</span>
                <span>{article.author}</span>
              </div>

              <h3
                className={`mt-3 font-semibold tracking-tight leading-[1.15] text-ink-950 group-hover:text-accent-700 transition-colors ${titleSize}`}
              >
                {meta.title}
              </h3>

              {size !== 'sm' && (
                <p className="mt-3 text-sm text-ink-600 leading-relaxed line-clamp-2">
                  {meta.excerpt}
                </p>
              )}

              <div className="mt-5 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 group-hover:text-accent-700 transition-colors">
                  <span className="relative">
                    {t('blog.readArticle')}
                    <motion.span
                      aria-hidden
                      className="absolute left-0 right-0 -bottom-0.5 h-[1.5px] bg-accent-600 origin-left"
                      initial={false}
                      animate={{ scaleX: hovered ? 1 : 0 }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </span>
                  <motion.svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    animate={{ x: hovered ? 4 : 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                  </motion.svg>
                </span>
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    </motion.div>
  )
}
